/**
 * Component for Transfers
 */

//  Generic imports
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import DOMPurify from "dompurify";

//  Motif imports
import MotifCard, { MotifCardHeader } from '@ey-xd/motif-react/Card';
import MotifButton, { MotifIconButton } from '@ey-xd/motif-react/Button';
import { MotifTooltip } from "@ey-xd/motif-react";
import MotifToast from '@ey-xd/motif-react/Toast';

//  Internal imports
import './Transfers.scss';
import TransferService from "../../../services/transferService";
import { setProjectTransfers, setActiveTransfer } from "../../../redux/userSlice";
import DiscardTransfer from "../../molecules/modals/discardTransfer/DiscardTransfer";

//  Icons imports
import iconWarningCircle from "../../../assets/images/warning_circle.svg";
import iconSearch from '../../../assets/images/search.svg';
import iconArrowBack from '../../../assets/images/arrow_back.svg';
import iconLoader from '../../../assets/images/Loading_indicator_circles.svg';
import iconArrow from '../../../assets/images/Iconarrow.svg';
import iconTrash from '../../../assets/images/Icontrash.svg';
import iconCheckCircle from "../../../assets/images/check_circle.svg";
import iconRefresh from '../../../assets/images/refresh.svg';
import infoEmpty from "../../../assets/images/info-empty.svg";

/* global Office */
import Utils from '../../../utils/Utils';

import { transfersConstants } from "../../../assets/data/constants";

export default function Transfers() {

    const dispatch = useDispatch();

    const { activeProject, user } = useSelector(state => state.user);

    const [expandedCardIndex, setExpandedCardIndex] = useState(null);
    const [transfers, setTransfers] = useState(null);
    const [sortedTransfers, setSortedTransfers] = useState([]);
    const [noTransfersFound, setNoTransfersFound] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const [isInsertDisabled, setIsInsertDisabled] = useState(true);
    const [showToastMessage, setShowToastMessage] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const [isRefreshInitiated, setIsRefreshInitiated] = useState(false);
    const [errorRefreshTransfers, setErrorRefreshTransfers] = useState(false);

    const utils = new Utils();
    const projectId = activeProject?.project_UUID;
    const transferService = new TransferService();
    const [discardModalShow, setDiscardModalShow] = useState(false);
    const [showDiscardToast, setShowDiscardToast] = useState(false);
    const [handlerAdded, setHandlerAdded] = useState(false);
    
    /**
     * Function to get transfers
     */
    useEffect(() => {
        getTransfersAsync();
        //  eslint-disable-next-line
    }, []);

    const getTransfersAsync = async () => {
        setShowLoader(true);
        const transfers = await transferService.getTransfers(projectId, user?.username);
        if (!transfers) {
            setTransfers(null);
            dispatch(setProjectTransfers({
                transfers: null
            }));
            setShowLoader(false);
            setErrorRefreshTransfers(true);
            setIsRefreshInitiated(false);
            return false;
        }
        setErrorRefreshTransfers(false);
        if (transfers.length === 0) {
            setNoTransfersFound(true);
            setTransfers(null);
            dispatch(setProjectTransfers({
                transfers: null
            }));
        } else {
            const filteredTransfers = transfers.filter((element) => ((element.isInsertDiscard !== 1) && (element.isInsertDiscard !== 2)));
            if (!filteredTransfers || filteredTransfers.length === 0) {
                setNoTransfersFound(true);
                setTransfers(null);
                dispatch(setProjectTransfers({
                    transfers: null
                }));
            } else {
                setTransfers(filteredTransfers);
                dispatch(setProjectTransfers({
                    transfers: filteredTransfers
                }));
                injectHandleInsertVisibility();
            }
        }
        setShowLoader(false);
        if (isRefreshInitiated) {
            setIsRefreshInitiated(false);
            setSuccessMessage('Refresh Complete');
            setShowToastMessage(true);
        }
    };

    /**
     * Function to sort transfers.
     */
    useEffect(() => {
        if (transfers && (transfers.length > 0)) {
            const sortedData = [...transfers].sort((a, b) => new Date(b.createTS) - new Date(a.createTS));
            setSortedTransfers(sortedData);
        } else {
            setSortedTransfers([]);
        }
    }, [transfers]);


    const handleCardClick = (vTransfer, index) => {
        setExpandedCardIndex(index);
        dispatch(setActiveTransfer({
            transfer: vTransfer
        }));
        injectHandleInsertVisibility();
    };

    const handleBackClick = () => {
        setExpandedCardIndex(null);
        dispatch(setActiveTransfer({
            transfer: null
        }));
    };

    /**
     * Hook to add document selection change handler
     */    
    useEffect(() => {
        injectHandleInsertVisibility();
        // eslint-disable-next-line
        return () => {
            if (handlerAdded) {
                Office.context.document.removeHandlerAsync(
                    "documentSelectionChanged",
                    { handler: handleInsertVisibility },
                    (res) => {
                        if (res.status !== Office.AsyncResultStatus.Failed) {
                            setHandlerAdded(false);
                        }
                    }
                );
            }
        };
        //  eslint-disable-next-line
    }, []);

    const injectHandleInsertVisibility = () => {
        if (handlerAdded) {
            return; // Exit if the handler is already added
        }
        if (Office.context.document) {
            Office.context.document.addHandlerAsync("documentSelectionChanged", handleInsertVisibility, (res) => {
                if (res.status !== Office.AsyncResultStatus.Failed) {
                    setHandlerAdded(true);
                } 
            });
        }
    };

    /**
     * Function to check whether placed cursor in PPT or selected any text
     * to enable or disable insert button
     */
    const handleInsertVisibility = () => {
        try {
            Office.context.document.getSelectedDataAsync(Office.CoercionType.Text, (res) => {
              if (res.status === Office.AsyncResultStatus.Failed) {
                setIsInsertDisabled(true);
              } else {
                setIsInsertDisabled(false);
              }
            });
        } catch (error) {}
    };

    const insertResponseToPowerpoint = async () => {
        setIsInsertDisabled(true);
        if (expandedCardIndex !== null) {
            try {
                await Office.context.document.setSelectedDataAsync(
                    sortedTransfers[expandedCardIndex].responseContent,
                    async (res) => 
                    {
                    if (res.status === Office.AsyncResultStatus.Failed) {
                        setIsInsertDisabled(false);
                        return false;
                    } else {
                        const isDiscardedResponse = await transferService.setInsertOrDiscardTransfers(sortedTransfers[expandedCardIndex], 1);
                        if (isDiscardedResponse) {
                            handleBackClick();
                            getTransfersAsync();
                            setSuccessMessage('Response inserted successfully');
                            setShowToastMessage(true);
                        }
                    }
                });
            } catch (error) {
                // Do nothing
            }
        }
        setIsInsertDisabled(false);
    };

    const discardTransferClick = () => {
        setDiscardModalShow(true);
    }

    const discardResponseTransfer = async() => {
        const isDiscardedResponse = await transferService.setInsertOrDiscardTransfers(sortedTransfers[expandedCardIndex], 2);
        if (isDiscardedResponse) {
            handleBackClick();
            getTransfersAsync();
            setShowDiscardToast(true);
            setDiscardModalShow(false);
        }
    }

    /**
     * Function will handle refresh of Transfers
     * @param {*} refresh 
     */
    const refreshTransfersList = () => {
        setIsRefreshInitiated(true);
    };

    /**
     * Hook to initiate refresh when user clicks refresh list
     */
    useEffect(() => {
        if (isRefreshInitiated) {
            getTransfersAsync();
        }
        //  eslint-disable-next-line
    }, [isRefreshInitiated]);

    const closeDiscardModal = () => {
        setDiscardModalShow(false);
    }

    return (
        <>
            {showLoader && (
                <div className="loaderDiv">
                    <div>
                        <img src={iconLoader} alt='Progress'
                            className="refreshIcon " />
                    </div>
                    <div className="refreshListText">
                        Refreshing List...
                    </div>
                </div>
            )}
            {
                sortedTransfers && (sortedTransfers.length > 0) && (expandedCardIndex === null) &&
                <div className="transfercard">
                    {sortedTransfers.map((transfer, index) => (
                        <MotifCard key={transfer.responseId} className="transfer-card"
                            onClick={() => handleCardClick(transfer, index)}
                        >
                            <div className="cardheader">
                                <span>{(transfer.sourceSystem === "DaaS_LineCommentary" || transfer.sourceSystem === "DaaS_TableAnalysis")  ? "DDA Excel Add-in" : "DDA Platform"}</span>
                            </div>


                            <div className="cardcontent"
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(utils.markdownToHtml(transfer.responseContent)),
                            }}
                              ></div>

                            
                                
                           
                            <hr className="divider" />
                            <div className="date">
                                {utils.formatDateString(transfer.createTS, 'Mmm DD, YYYY HH:MM (UTC)')}
                            </div>
                        </MotifCard>
                    ))}
                </div>
            }
            {
                sortedTransfers && (sortedTransfers.length > 0) && (expandedCardIndex !== null) &&
                <div className="transfercard expandedview">

                    <MotifCard className="expanded-card">
                        < MotifCardHeader className="expanded-header">
                            <div className="arrowText">
                                <MotifIconButton
                                    aria-label="Click here to go back to Project Dashboard"
                                    onClick={handleBackClick}
                                >
                                    <img src={iconArrowBack} alt='Previous' />
                                </MotifIconButton>
                                <span className="backButtonText"> Back</span>
                            </div>
                            <span className="source-system">{(sortedTransfers[expandedCardIndex].sourceSystem ===  "DaaS_LineCommentary" || sortedTransfers[expandedCardIndex].sourceSystem ==="DaaS_TableAnalysis")  ? "DDA Excel Add-in" : "DDA Platform"  }</span>
                        </MotifCardHeader>
                        <div className="expanded-content">
                            <p className="transfer-content"
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(utils.markdownToHtml(sortedTransfers[expandedCardIndex].responseContent)),
                            }}
                              ></p>
                        </div>
                    </MotifCard>
                </div>
            }
            {
                !(sortedTransfers && (sortedTransfers.length > 0)) &&
                <div className="parent-notransfers-div">
                    {
                        getContent()
                    }
                </div>
            }
            <div className="genericFooter">
                <div className="footerInner alignEnd">
                    {expandedCardIndex === null &&
                        <MotifButton className="refreshButton" variant="secondary" id="btnRefreshProjectsList" onClick={() => { refreshTransfersList() }} type="button">
                            <img className="refresh-icon" src={iconRefresh} alt="refresh peojects icon" />
                            <span className="textEllipsis">Refresh</span>
                        </MotifButton>
                    }
                    {expandedCardIndex != null &&
                        <React.Fragment>
                            <MotifTooltip
                                hide={!isInsertDisabled}
                                trigger={
                                    <MotifButton
                                        className="btnInsert"
                                        variant="primary"
                                        disabled={isInsertDisabled}
                                        onClick={() => insertResponseToPowerpoint()}
                                    >
                                        <img src={iconArrow} alt="Insert" className="iconInsert" />
                                        <span>Insert</span>
                                    </MotifButton>
                                }
                                contentClassName="tooltipWithoutClose"
                                allowHover={true}
                                allowClick={false}
                                hideCloseButton={true}
                            >
                                Select within a text<br />box to continue
                            </MotifTooltip>
                            <MotifButton 
                                className="btnDiscard" 
                                variant="secondary" 
                                onClick={() => discardTransferClick()} 
                            >
                                <img src={iconTrash} alt="Discard" className="Discard" />
                                <span>Discard</span>
                            </MotifButton>
                        </React.Fragment>
                    }
                </div>
            </div>
            {
                showToastMessage && !errorRefreshTransfers &&
                <div className="toast-div">
                    <MotifToast
                        onClose={() => setShowToastMessage(false)}
                        icons={{ toast: <img src={iconCheckCircle} alt="Success" /> }}
                        variant="success"

                    >
                        {successMessage}
                    </MotifToast>
                </div>
            }
            {
                showDiscardToast &&
                <div className="toast-div">
                    <MotifToast
                        onClose={() => setShowDiscardToast(false)}
                        icons={{toast: <img src={infoEmpty} alt="Info" />}}
                    >
                        Response discarded successfully
                    </MotifToast>
                </div>
            }
            {
                discardModalShow && 
                <div className="discard-modal-container">
                    <DiscardTransfer
                        discardModalShow={discardModalShow}
                        closeDiscardModal={() => closeDiscardModal()}
                        discardResponseTransfer={() => discardResponseTransfer()}
                        discardTitle={transfersConstants?.discardTitle}
                        discardMessage={transfersConstants?.discardMessage}
                    />
                </div>
            }
        </>
    );

    function getContent() {
        if (errorRefreshTransfers && !showLoader) {
            return (
                <div>
                    <div className="parent-search-icon-div">
                        <div className="search-icon-div">
                            <img src={iconWarningCircle} alt="error" />
                        </div>
                    </div>
                    <div className="parent-notransfers-text-div">
                        System error
                    </div>
                    <div className="parent-statement-div">
                        Click on 'Refresh' or reload the Add-in
                    </div>
                </div>
            )
        }
        else if (noTransfersFound) {
            return (
                <div>
                    <div className="parent-search-icon-div">
                        <div className="search-icon-div">
                            <img src={iconSearch} alt="Search" />
                        </div>
                    </div>
                    <div className="parent-notransfers-text-div">
                        No transfers found
                    </div>
                    <div className="parent-statement-div">
                        Transfer a response from the Digital Diligence Assistant<br /> Platform to get started
                    </div>
                </div>
            )
        }
    };
}